/**
 * @module modules/pageStateStack
 * @description Page state management system using sessionStorage and browser history.
 * Tracks navigation history and maintains state for each page in the navigation stack.
 * Handles history pruning on forward navigation and state persistence across page reloads.
 */

import { globalHistory } from '@reach/router';

import { isSessionStorageAvailable, safeParse } from 'modules/utils';

/**
 * @const ORIGIN_KEY
 * @description Special key used to identify the initial page load in the history stack
 * @type {string}
 */
export const ORIGIN_KEY = 'ORIGIN_KEY';

/**
 * @const STORAGE_KEY
 * @private
 * @description SessionStorage key for persisting the page state stack
 * @type {string}
 */
const STORAGE_KEY = 'page-state-stack';

let pageStateStack = [
  // there's no state on first landing,
  // but there IS a key on page reload
  { key: window.history?.state?.key || ORIGIN_KEY },
];

if (isSessionStorageAvailable()) {
  const _pageStateStack = safeParse(sessionStorage.getItem(STORAGE_KEY));
  if (_pageStateStack) {
    pageStateStack = _pageStateStack;
  }
}

/**
 * @function updateStorage
 * @private
 * @description Persist current pageStateStack to sessionStorage for cross-reload persistence
 */
const updateStorage = () =>
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(pageStateStack));

/**
 * @function pruneHistory
 * @private
 * @description Prune the navigation history stack from the previous key forward.
 * Called on push actions to remove forward history when user navigates to a new page,
 * mimicking browser's native forward history clearing behavior.
 * @param {string} previousKey - History key from the previous navigation state
 */
const pruneHistory = (previousKey) => {
  const lastKeyInStack = pageStateStack[pageStateStack.length - 1]?.key;

  if (previousKey && lastKeyInStack !== previousKey) {
    const index = pageStateStack.findIndex(({ key }) => key === previousKey);
    pageStateStack = pageStateStack.slice(0, index + 1);
  }
};

let previousKey;
let globalListenerTimeout;
globalHistory.listen(({ action, location }) => {
  clearTimeout(globalListenerTimeout);
  globalListenerTimeout = setTimeout(() => {
    const key = location?.state?.key || ORIGIN_KEY;

    // on pop actions, there will already an entry with this key,
    // so we're only concerned with push actions
    if (action.toUpperCase() !== 'POP') {
      pruneHistory(previousKey);
      pageStateStack.push({ key });
      updateStorage();
    }
    previousKey = key;
  }, 240);
});

/**
 * @function isObject
 * @private
 * @description Check if a value is a plain object (not array or null)
 * @param {*} obj - Value to test
 * @returns {boolean} True if value is a plain object
 */
const isObject = (obj) =>
  typeof obj === 'object' && !Array.isArray(obj) && obj !== null;

/**
 * @function setPageState
 * @description Set or update state for a specific page in the navigation history.
 * If the page key doesn't exist, adds a new entry to the stack. If it exists, updates its state.
 * Automatically persists changes to sessionStorage.
 * @param {string} key - History key identifying the page
 * @param {*} state - State object to store for this page
 */
export const setPageState = (key, state) => {
  const stateItem = pageStateStack.reverse().find((item) => item.key === key);
  if (!stateItem) {
    pageStateStack.push({ key, state, href: window.location.href });
    updateStorage();
    return;
  }

  stateItem.state = state;
};

/**
 * @function getCurrentPageState
 * @description Retrieve the state for the current page from the navigation history.
 * Uses the current window.history.state.key to lookup the corresponding state in the stack.
 * @returns {Object} State object for current page, or empty object if no state exists
 */
export const getCurrentPageState = () => {
  const key = window.history.state?.key || ORIGIN_KEY;
  const element = pageStateStack.reverse().find((item) => item.key === key);
  return isObject(element?.state) ? element.state : {};
};
